import { useEffect, useRef, useCallback } from "react";
import { useAuth } from "../context/AuthContext";

export const useBordeiroEvents = (
  onEvent: () => void,
  lojaId?: number | null,
  onBordaEmFalta?: (bordaNome: string) => void,
  options?: {
    onAvailabilityChange?: (available: boolean) => void;
    /** Quando false, fecha a conexão SSE (ex.: pizzaiolo offline). Default: true */
    enabled?: boolean;
  }
) => {
  const { token } = useAuth();
  const eventSourceRef = useRef<EventSource | null>(null);
  const reconnectTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const reconnectAttempts = useRef(0);
  const maxReconnectAttempts = 5;
  const reconnectDelay = 3000;
  const onAvailabilityChangeRef = useRef(options?.onAvailabilityChange);
  onAvailabilityChangeRef.current = options?.onAvailabilityChange;
  const enabledRef = useRef(options?.enabled !== false);
  enabledRef.current = options?.enabled !== false;

  const notifyAvailability = useCallback((available: boolean) => {
    onAvailabilityChangeRef.current?.(available);
  }, []);

  const cleanupConnection = useCallback(() => {
    if (eventSourceRef.current) {
      eventSourceRef.current.close();
      eventSourceRef.current = null;
    }
    if (reconnectTimeoutRef.current) {
      clearTimeout(reconnectTimeoutRef.current);
      reconnectTimeoutRef.current = null;
    }
  }, []);

  const connect = useCallback(() => {
    if (!token || !enabledRef.current) return;

    if (eventSourceRef.current) {
      eventSourceRef.current.close();
    }

    const apiUrl = process.env.REACT_APP_API_URL || "http://localhost:3001";
    const params = new URLSearchParams();
    if (lojaId) {
      params.append("lojaId", lojaId.toString());
    }
    params.append("token", token);

    const url = `${apiUrl}/events/ranking/fila-integracao?${params.toString()}`;

    const eventSource = new EventSource(url, {
      withCredentials: false,
    });

    eventSource.onopen = () => {
      reconnectAttempts.current = 0;
      notifyAvailability(true);
      onEvent();
    };

    eventSource.addEventListener("connected", () => {
      reconnectAttempts.current = 0;
      notifyAvailability(true);
      onEvent();
    });

    eventSource.addEventListener("filaIntegracao.updated", () => {
      onEvent();
    });

    eventSource.addEventListener("paozinhoPreparo.updated", () => {
      onEvent();
    });

    eventSource.addEventListener("bordaEmFalta.updated", (e: MessageEvent) => {
      try {
        const data = typeof e.data === "string" ? JSON.parse(e.data) : e.data;
        if (data?.bordaNome && typeof onBordaEmFalta === "function") {
          onBordaEmFalta(String(data.bordaNome).trim());
        }
        onEvent();
      } catch {
        onEvent();
      }
    });

    eventSource.onerror = (error) => {
      console.error("SSE error for bordeiro:", error);
      eventSource.close();

      if (reconnectAttempts.current < maxReconnectAttempts) {
        reconnectAttempts.current += 1;
        reconnectTimeoutRef.current = setTimeout(() => {
          connect();
        }, reconnectDelay * reconnectAttempts.current);
      } else {
        notifyAvailability(false);
      }
    };

    eventSourceRef.current = eventSource;
  }, [token, lojaId, onEvent, onBordaEmFalta, notifyAvailability]);

  const enabled = options?.enabled !== false;

  useEffect(() => {
    if (!enabled) {
      cleanupConnection();
      notifyAvailability(true);
      return;
    }

    connect();

    return () => {
      cleanupConnection();
    };
  }, [connect, enabled, cleanupConnection, notifyAvailability]);
};
